import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { Automapper } from '@core/api/mapper';

import { LanguageState, LanguageStore } from './language.store';
import { Language, Partner } from '@core/models/partner.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LocaleId } from '@core/config';
import { ApiService } from '@core/api/api.service';
import { UserNamespacedResourceBaseUrlBuilder } from '@core/user-namespaced-resource-base-url-builder.service';
import { ThemeSwitcherService } from '@feature/theme-manager/theme-switcher/theme-switcher.service';
import { LanguageChangeService } from './language-change.service';
import { HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class LanguageService extends ApiEntityService<LanguageState> {
  private readonly LANGUAGE_STORAGE_KEY = 'wai_user_language';
  private readonly LANGUAGE_EXPLICITLY_SET_KEY = 'wai_language_explicitly_set';
  private currentLanguageSubject = new BehaviorSubject<LocaleId>('en');
  private availableLanguagesSubject = new BehaviorSubject<Language[]>([]);

  public readonly currentLanguage$: Observable<LocaleId> = this.currentLanguageSubject.asObservable();
  public readonly availableLanguages$: Observable<Language[]> = this.availableLanguagesSubject.asObservable();
  constructor(
    store: LanguageStore,
    urlBuilder: UserNamespacedResourceBaseUrlBuilder,
    private translateService: TranslateService,
    private apiService: ApiService,
    private themeSwitcherService: ThemeSwitcherService,
    private languageChangeService: LanguageChangeService,
  ) {
    super(store, urlBuilder, Automapper.create(Partner));
    this.overrideResourceName('language');

    this.initializeLanguage();
    this.initializeAvailableLanguages();

    this.themeSwitcherService.activeTheme$.subscribe((theme: Partner) => {
      if (theme?.availableLanguages && theme.availableLanguages.length > 0) {
        const firstItem = theme.availableLanguages[0];

        if (typeof firstItem === 'string') {
          const languageCodes = theme.availableLanguages as unknown as string[];
          this.convertLanguageCodesToObjects(languageCodes).subscribe((languages: Language[]) => {
            if (languages.length > 0) {
              this.availableLanguagesSubject.next(languages);
              const themeData = { ...theme, availableLanguages: languages };
              localStorage.setItem('initialThemeParams', JSON.stringify(themeData));
              const wasLanguageExplicitlySet = localStorage.getItem(this.LANGUAGE_EXPLICITLY_SET_KEY);
              if (!wasLanguageExplicitlySet) {
                const defaultLanguage = languages.find((lang) => lang.isDefault);
                if (defaultLanguage && defaultLanguage.code !== this.currentLanguageSubject.value) {
                  this.currentLanguageSubject.next(defaultLanguage.code as LocaleId);
                  this.translateService.use(defaultLanguage.code);
                  this.storeLanguage(defaultLanguage.code as LocaleId);
                }
              }
            }
          });
        } else {
          const validLanguages = theme.availableLanguages.filter((lang) => lang && lang.code);

          if (validLanguages.length > 0) {
            this.availableLanguagesSubject.next(validLanguages);
            const themeData = { ...theme, availableLanguages: validLanguages };
            localStorage.setItem('initialThemeParams', JSON.stringify(themeData));

            const wasLanguageExplicitlySet = localStorage.getItem(this.LANGUAGE_EXPLICITLY_SET_KEY);

            if (!wasLanguageExplicitlySet) {
              const defaultLanguage = validLanguages.find((lang) => lang.isDefault);

              if (defaultLanguage && defaultLanguage.code !== this.currentLanguageSubject.value) {
                this.currentLanguageSubject.next(defaultLanguage.code as LocaleId);
                this.translateService.use(defaultLanguage.code);
                this.storeLanguage(defaultLanguage.code as LocaleId);
              }
            }
          }
        }
      }
    });

    this.translateService.onLangChange.subscribe((event) => {
      const newLang = event.lang as LocaleId;
      if (newLang && newLang !== this.currentLanguageSubject.value) {
        this.currentLanguageSubject.next(newLang);
        this.storeLanguage(newLang);
      }
    });
  }

  fetchLanguages() {
    // NOTE: This method is only for superuser administration
    // General language fetching is handled by the theme endpoint
    // const baseUrl = this.urlBuilder.buildBaseUrl();
    // const url = `${baseUrl}languages/`;
    const url = `languages/`;
    return this.apiService.get(url).pipe(
      map((response: any) => {
        // Extract the results array from the paginated response
        return response.results || response;
      }),
    );
  }

  // NOTE: The following methods are for superuser administration only
  // They require admin permissions and are not used for general language display

  deleteLanguage(lang) {
    // const baseUrl = this.urlBuilder.buildBaseUrl();
    // const url = `${baseUrl}languages/${lang.uid}`;
    const url = `languages/${lang.uid}`;
    return this.apiService.delete(url);
  }

  createLanguage(languageData: any) {
    // const baseUrl = this.urlBuilder.buildBaseUrl();
    // const url = `${baseUrl}languages/`;
    const url = `languages/`;
    return this.apiService.post(url, languageData);
  }

  updateLanguage(languageUid: string, languageData: any) {
    // const baseUrl = this.urlBuilder.buildBaseUrl();
    // const url = `${baseUrl}languages/${languageUid}`;
    const url = `languages/${languageUid}`;
    return this.apiService.patch(url, languageData);
  }

  translateLanguage(languageUid: string) {
    const url = `languages/${languageUid}/translate/`;
    return this.apiService.post(url, {});
  }

  forceTranslateLanguage(languageUid: string) {
    const url = `languages/${languageUid}/translate/`;
    const params = new HttpParams().set('force_update', 'true');
    return this.apiService.post(url, {}, { params });
  }

  /**
   * Initialize the language system with stored preference or default.
   * NOTE: Does NOT call translateService.use() to avoid duplicate HTTP requests.
   * AppTranslateModule has already initialized the TranslateService.
   */
  private initializeLanguage(): void {
    const storedLanguage = this.getStoredLanguage();

    if (storedLanguage) {
      // User has a stored preference, use it and mark as explicitly set
      this.currentLanguageSubject.next(storedLanguage);
      // DON'T call translateService.use() - AppTranslateModule already did it
      // This prevents duplicate HTTP calls to load translations
      localStorage.setItem(this.LANGUAGE_EXPLICITLY_SET_KEY, 'true');
    } else {
      // No stored preference - use current translate service language
      // AppTranslateModule has already initialized translateService with correct language
      const currentLang = (this.translateService.currentLang as LocaleId) || 'en';
      this.currentLanguageSubject.next(currentLang);
      // DON'T store language yet - let theme default override if available
    }
  }

  public getCurrentLanguage(): LocaleId {
    return this.currentLanguageSubject.value;
  }

  public setLanguage(language: LocaleId): void {
    if (language !== this.currentLanguageSubject.value) {
      const previousLanguage = this.currentLanguageSubject.value;

      this.currentLanguageSubject.next(language);
      this.translateService.use(language);
      this.storeLanguage(language);

      // Mark that user has explicitly set a language (not from browser default)
      localStorage.setItem(this.LANGUAGE_EXPLICITLY_SET_KEY, 'true');

      // Notify about language change to trigger backend data refresh
      this.languageChangeService.notifyLanguageChange(previousLanguage, language);
    }
  }

  public resetToDefault(): void {
    this.setLanguage('en');
  }

  private initializeAvailableLanguages(): void {
    const storedLanguages = this.getAvailableLanguagesFromStorage();
    if (storedLanguages && storedLanguages.length > 0) {
      this.availableLanguagesSubject.next(storedLanguages);
    }
    if (!storedLanguages || storedLanguages.length === 0) {
      this.waitForThemeAndLoadLanguages();
    }
  }

  private getAvailableLanguagesFromStorage(): Language[] {
    try {
      const initialThemeParams = localStorage.getItem('initialThemeParams');
      if (initialThemeParams) {
        const themeData = JSON.parse(initialThemeParams);
        if (themeData.availableLanguages && themeData.availableLanguages.length > 0) {
          const firstItem = themeData.availableLanguages[0];

          if (typeof firstItem === 'string') {
            console.warn('Old language format detected in localStorage, clearing and using fallback');
            localStorage.removeItem('initialThemeParams');
          } else if (firstItem && typeof firstItem === 'object' && firstItem.code) {
            return themeData.availableLanguages;
          }
        }
      }
    } catch (error) {
      console.error('Error reading languages from localStorage:', error);
    }

    return [
      {
        id: 1,
        uid: 'en-default',
        code: 'en',
        name: 'English',
        isDefault: true,
        isActive: true,
        nativeName: 'English',
        flagEmoji: '🇺🇸',
        rtl: false,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      },
      {
        id: 2,
        uid: 'de-default',
        code: 'de',
        name: 'German',
        isDefault: false,
        isActive: true,
        nativeName: 'Deutsch',
        flagEmoji: '🇩🇪',
        rtl: false,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      },
    ];
  }

  public getAvailableLanguages(): Language[] {
    return this.availableLanguagesSubject.value;
  }

  public refreshAvailableLanguages(): void {
    this.themeSwitcherService.fetchTheme();
    setTimeout(() => {
      this.waitForThemeAndLoadLanguages();
    }, 500);
  }

  private waitForThemeAndLoadLanguages(): void {
    this.themeSwitcherService.activeTheme$.pipe(take(1)).subscribe((theme: Partner) => {
      if (theme?.availableLanguages && theme.availableLanguages.length > 0) {
        const validLanguages = theme.availableLanguages.filter((lang) => lang && lang.code);
        if (validLanguages.length > 0) {
          this.availableLanguagesSubject.next(validLanguages);
          const themeData = { ...theme, availableLanguages: validLanguages };
          localStorage.setItem('initialThemeParams', JSON.stringify(themeData));
        }
      }
    });
  }

  public isCurrentLanguage(language: LocaleId): boolean {
    return this.currentLanguageSubject.value === language;
  }

  public getLanguageDisplayName(language: LocaleId): string {
    const names: Record<LocaleId, string> = {
      de: 'Deutsch',
      en: 'English',
      ch: '中文',
      co: 'Español (Colombia)',
      gr: 'Ελληνικά',
      it: 'Italiano',
      ja: '日本語',
      pl: 'Polski',
      sp: 'Español',
    };
    return names[language] || language;
  }

  private getStoredLanguage(): LocaleId | null {
    try {
      const stored = localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
      if (stored) {
        return stored as LocaleId;
      }
    } catch (error) {}
    return null;
  }

  private storeLanguage(language: LocaleId): void {
    try {
      localStorage.setItem(this.LANGUAGE_STORAGE_KEY, language);
    } catch (error) {}
  }

  public clearStoredLanguage(): void {
    try {
      localStorage.removeItem(this.LANGUAGE_STORAGE_KEY);
      localStorage.removeItem(this.LANGUAGE_EXPLICITLY_SET_KEY);
    } catch (error) {}
  }

  public refreshFromStorage(): void {
    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage && storedLanguage !== this.currentLanguageSubject.value) {
      this.setLanguage(storedLanguage);
    }
  }

  public handleLogout(): void {
    const currentLang = this.currentLanguageSubject.value;
    if (currentLang) {
      this.storeLanguage(currentLang);
    }
  }

  public restoreLanguage(): void {
    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage) {
      this.setLanguage(storedLanguage);
    } else {
      this.setLanguage('en');
    }
  }

  /**
   * Convert language codes to full Language objects
   * This is an internal method to avoid circular dependencies
   * @param languageCodes Array of language codes (e.g., ['en', 'de', 'fr'])
   * @returns Observable of Language objects
   */
  private convertLanguageCodesToObjects(languageCodes: string[]): Observable<Language[]> {
    if (!languageCodes || languageCodes.length === 0) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    return this.fetchLanguages().pipe(
      map((allLanguages: Language[]) => {
        const languageMap = new Map<string, Language>();
        allLanguages.forEach((lang) => {
          if (lang && lang.code) {
            languageMap.set(lang.code, lang);
          }
        });

        const convertedLanguages: Language[] = [];
        languageCodes.forEach((code) => {
          const language = languageMap.get(code);
          if (language) {
            convertedLanguages.push(language);
          }
        });

        return convertedLanguages;
      }),
    );
  }
}
